import React, { useCallback, useEffect, useState } from 'react';

import AppBar from 'components/AppBar';
import ApprovalCardType from 'components/ApprovalCardType';
import Button from 'components/Buttons/Button';
import ProgressSteps from 'components/ProgressSteps';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { formatTrim } from 'utils/generic';

import { Container, PaddingContainer, Header, InfoContainer, GroupButton } from '../../styles';

interface Props {
  wizard: WizardProps;
}

const ApprovalOrderOnboarding = ({ wizard }: Props) => {
  useEffect(() => {
    if (!(wizard.data.approvalType === 'PARALLEL' || wizard.data.approvalType === 'SEQUENTIAL')) {
      wizard.nextStep();
    }
  }, [wizard]);

  const [hasSelected, setHasSelected] = useState(wizard.data.hasSelectedApprovalOrder);
  const [selectNoOrder, setSelectNoOrder] = useState(() => {
    if (wizard.data.approvalType === 'PARALLEL') {
      return wizard.data.hasSelectedApprovalOrder;
    }
    return false;
  });

  const { t } = useStoreTranslation();

  const handleSelectUser = useCallback(
    (value: string) => {
      const selectListApprovers = wizard.data.approvers.map((approval: any) => {
        if (formatTrim(approval.email) === formatTrim(value)) {
          return {
            ...approval,
            isSelected: true,
            sequence: 1,
          };
        }
        return {
          ...approval,
          isSelected: false,
          sequence: 2,
        };
      });

      setSelectNoOrder(false);
      setHasSelected(true);
      wizard.setData({
        ...wizard.data,
        approvers: selectListApprovers,
        approvalType: 'SEQUENTIAL',
        hasSelectedApprovalOrder: true,
      });
    },
    [wizard],
  );

  const handleSelectNoOrder = useCallback(() => {
    const selectListApprovers = wizard.data.approvers.map((approval: any) => ({
      ...approval,
      isSelected: false,
      sequence: 2,
    }));

    setSelectNoOrder(true);
    setHasSelected(true);
    wizard.setData({
      ...wizard.data,
      approvers: selectListApprovers,
      approvalType: 'PARALLEL',
      hasSelectedApprovalOrder: true,
    });
  }, [wizard]);

  return (
    <Container>
      <nav>
        <AppBar goBack={() => wizard.previousStep()}>{t('approvalOrderOnboardingForm.title')}</AppBar>
        <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
      </nav>
      <PaddingContainer>
        <Header>
          <h3>{t('approvalOrderOnboardingForm.headerSelectState')}</h3>
        </Header>
        {wizard.data.approvers.map((approval: any) => (
          <ApprovalCardType
            key={formatTrim(approval.email)}
            type={approval.email}
            icon="/corporate/images/user-icon.svg"
            isSelected={approval.isSelected}
            onClick={handleSelectUser}
          >
            <h3>
              {t('approvalOrderOnboardingForm.approvalSequenceYes')}
              <strong>{` ${approval.name} `}</strong>
              {t('approvalOrderOnboardingForm.approvalSequence')}
            </h3>
          </ApprovalCardType>
        ))}
        <ApprovalCardType
          type={wizard.data.approvalType}
          icon="/corporate/images/group-check.svg"
          title={t('approvalOrderOnboardingForm.noOrder')}
          isSelected={selectNoOrder}
          onClick={handleSelectNoOrder}
        />
        {!hasSelected && (
          <InfoContainer type="leftTop">
            <p>{t('approvalOrderOnboardingForm.infoSelectState')}</p>
          </InfoContainer>
        )}
      </PaddingContainer>
      {hasSelected && (
        <GroupButton>
          <Button
            onClick={() => {
              wizard.nextStep();
            }}
            disabled={false}
            value={t('approvalOrderOnboardingForm.nextButton')}
          />
        </GroupButton>
      )}
    </Container>
  );
};

ApprovalOrderOnboarding.defaultProps = {
  wizard: {},
};

export default ApprovalOrderOnboarding;
